function resolveHandler(binding, vnode) {
  const { handler } = binding.value || {}

  if (typeof handler === 'function') {
    return handler
  }

  if (typeof handler === 'string' && vnode.context?.[handler]) {
    return vnode.context[handler].bind(vnode.context)
  }

  return null
}

function resolveExcludedElements(binding, vnode) {
  const { exclude = [] } = binding.value || {}

  // `exclude` can contain component ref names or raw DOM elements.
  return exclude
    .map(item => {
      if (!item) {
        return null
      }

      if (item instanceof Element) {
        return item
      }

      if (typeof item === 'string') {
        return vnode.context?.$refs?.[item] || null
      }

      return null
    })
    .filter(Boolean)
}

function isOpen(binding) {
  const value = binding.value || {}

  // Preserve the old behavior for existing usages: if `isOpen` is not passed,
  // the directive stays active.
  if (typeof value.isOpen === 'boolean') {
    return value.isOpen
  }

  return true
}

function isElementVisible(el) {
  // Ignore hidden DOM instances. This matters in responsive layouts where
  // desktop and mobile markup can exist at the same time but one is hidden by CSS.
  return Boolean(el?.isConnected && el.getClientRects().length)
}

function createOutsideClickHandler(el, binding, vnode) {
  return event => {
    // Outside-click should only run for open, visible elements.
    if (!isOpen(binding) || !isElementVisible(el)) {
      return
    }

    const excludedElements = resolveExcludedElements(binding, vnode)
    const clickedOnExcludedEl = excludedElements.some(excludedEl => excludedEl.contains(event.target))
    // These ids are used by the mobile menu toggle and should not close closable UI.
    const menuItemsArray = ['open-mobile-menu', 'close-mobile-menu', 'menu-toggle-icon', 'middle-menu-line']

    // Ignore clicks inside the bound element or inside explicitly excluded elements.
    if (el.contains(event.target) || clickedOnExcludedEl || menuItemsArray.includes(event.target?.id)) {
      return
    }

    const handler = resolveHandler(binding, vnode)
    if (handler) {
      handler(event)
    }
  }
}

export default {
  bind(el, binding, vnode) {
    // Store the current listener on the element so Vue can replace/remove it later.
    el.__closable__ = createOutsideClickHandler(el, binding, vnode)

    document.addEventListener('click', el.__closable__)
    document.addEventListener('touchstart', el.__closable__)
  },
  update(el, binding, vnode) {
    // Rebuild the listener so it always uses the latest binding values.
    document.removeEventListener('click', el.__closable__)
    document.removeEventListener('touchstart', el.__closable__)

    el.__closable__ = createOutsideClickHandler(el, binding, vnode)

    document.addEventListener('click', el.__closable__)
    document.addEventListener('touchstart', el.__closable__)
  },
  unbind(el) {
    // Clean up document-level listeners when the bound element is destroyed.
    document.removeEventListener('click', el.__closable__)
    document.removeEventListener('touchstart', el.__closable__)
    delete el.__closable__
  },
}
